import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { wmsLayersService } from '../../services/wmsLayersService'
import type { RootState } from './reducers'
import type { WMSLayer, WMSLayersState, WMSLayerError } from '@popety-io/types'

// Initial state
const initialState: WMSLayersState = {
  layers: [],
  loading: false,
  error: null,
  lastFetch: null,
  previousBasemap: null,
  activeWMSLayerId: null,
}

// Async thunk for fetching all WMS layers
export const fetchWMSLayers = createAsyncThunk<
  WMSLayer[],
  void,
  { rejectValue: WMSLayerError }
>('wmsLayers/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const layers = await wmsLayersService.getAll()
    return layers
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || 'Failed to fetch WMS layers',
      code: error.code,
      details: error.response?.data,
    })
  }
})

// Create slice
const wmsLayersSlice = createSlice({
  name: 'wmsLayers',
  initialState,
  reducers: {
    // Action to update a single layer's properties (e.g., visibility, opacity)
    updateLayer: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<WMSLayer> }>
    ) => {
      const { id, updates } = action.payload
      const layerIndex = state.layers.findIndex((layer) => layer.id === id)
      if (layerIndex !== -1) {
        state.layers[layerIndex] = { ...state.layers[layerIndex], ...updates }
      }
    },
    // Action to set multiple layers at once
    setLayers: (state, action: PayloadAction<WMSLayer[]>) => {
      state.layers = action.payload
    },
    // Action to clear error
    clearError: (state) => {
      state.error = null
    },
    // Action to reset state
    resetState: () => initialState,
    // Action to set active WMS layer
    setActiveWMSLayer: (
      state,
      action: PayloadAction<{ layerId: string; previousBasemap?: string }>
    ) => {
      state.activeWMSLayerId = action.payload.layerId
      if (action.payload.previousBasemap) {
        state.previousBasemap = action.payload.previousBasemap
      }
    },
    // Action to clear active WMS layer
    clearActiveWMSLayer: (state) => {
      state.activeWMSLayerId = null
      state.previousBasemap = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchWMSLayers pending
      .addCase(fetchWMSLayers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      // Handle fetchWMSLayers fulfilled
      .addCase(fetchWMSLayers.fulfilled, (state, action) => {
        state.loading = false
        state.layers = action.payload
        state.lastFetch = Date.now()
        state.error = null
      })
      // Handle fetchWMSLayers rejected
      .addCase(fetchWMSLayers.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.message ||
          'An error occurred while fetching WMS layers'
      })
  },
})

// Export actions
export const {
  updateLayer,
  setLayers,
  clearError,
  resetState,
  setActiveWMSLayer,
  clearActiveWMSLayer,
} = wmsLayersSlice.actions

// Export reducer
export default wmsLayersSlice.reducer
export const wmsLayersReducer = { wmsLayers: wmsLayersSlice.reducer }

// Selectors
export const selectWMSLayers = (state: RootState) => state.wmsLayers.layers
export const selectWMSLayersLoading = (state: RootState) =>
  state.wmsLayers.loading
export const selectWMSLayersError = (state: RootState) => state.wmsLayers.error
export const selectWMSLayersLastFetch = (state: RootState) =>
  state.wmsLayers.lastFetch

// Selector to get a specific layer by ID
export const selectWMSLayerById = (id: string) => (state: RootState) =>
  state.wmsLayers.layers.find((layer) => layer.id === id)

// Selector to check if data needs refresh (e.g., older than 5 minutes)
export const selectWMSLayersNeedsRefresh = (state: RootState) => {
  const { lastFetch } = state.wmsLayers
  if (!lastFetch) return true
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
  return lastFetch < fiveMinutesAgo
}

// Selector to get land/environmental layers (mapTopicId === "2")
export const selectEnvironmentalWMSLayers = (state: RootState) =>
  state.wmsLayers.layers

// Selector to get active WMS layer ID
export const selectActiveWMSLayerId = (state: RootState) =>
  state.wmsLayers.activeWMSLayerId

// Selector to get previous basemap
export const selectPreviousBasemap = (state: RootState) =>
  state.wmsLayers.previousBasemap

// Selector to get active WMS layer object
export const selectActiveWMSLayer = (state: RootState) => {
  const activeId = state.wmsLayers.activeWMSLayerId
  if (!activeId) return null
  return state.wmsLayers.layers.find((layer) => layer.id === activeId) || null
}
